import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { selectPatient, updateDemographics } from '../../../redux/assessment';
import { useAppDispatch } from '../../../pages/assessment';
import { selectIsGuardian } from '../../../redux/session';
import { DemographicsDiscovery } from './demographics-discovery';
import { AssessmentButtons } from '../shared';
import { PreferredNameAndPronouns } from './preferred-name-and-pronouns';
import { setLabel } from '../../../redux/meta';

export interface PersonalDetailFormValues {
  preferredName: string;
  patientPronouns: string;
  discoveryChannel: string;
  discoveryChannelOther: string;
}

const PersonalDetails = () => {
  const isGuardian = useSelector(selectIsGuardian);
  const { addToast } = useToasts();
  const dispatch = useAppDispatch();
  const patient = useSelector(selectPatient);

  const form = useForm<PersonalDetailFormValues>({
    mode: 'onBlur',
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    watch,
  } = form;

  const title = isGuardian
    ? 'Let’s start with the basics'
    : 'Tell us a little about yourself';

  useEffect(() => {
    dispatch(setLabel(title));
  }, [title]);

  const onSubmit: SubmitHandler<PersonalDetailFormValues> = async data => {
    try {
      const formattedDiscoveryChannel =
        data.discoveryChannel === 'Other' && data.discoveryChannelOther
          ? `Other: ${data.discoveryChannelOther}`
          : data.discoveryChannel;

      const dataToSend = {
        preferredName: data.preferredName,
        patientPronouns: data.patientPronouns,
        discoveryChannel: formattedDiscoveryChannel,
      };
      await dispatch(updateDemographics(dataToSend));
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PreferredNameAndPronouns control={control} patient={patient} />
      <DemographicsDiscovery
        control={control}
        patient={patient}
        isVisible
        showOtherField={watch('discoveryChannel') === 'Other'}
      />
      <AssessmentButtons
        primary={{
          children: "See if we're in your state",
          type: 'submit',
          disabled: !isValid || isSubmitting,
          loading: isSubmitting,
        }}
      />
    </form>
  );
};

export default PersonalDetails;
